import { z } from 'zod';
import { moneySchema } from './common.schemas';
import {
  EXPENSE_STATES,
  EXPENSE_SUBJECT_MESSAGE,
  expenseCategoryCodeSchema,
  expenseFuelDetailsSchema,
  expenseReceiptConfirmRequestSchema,
  expenseReceiptConfirmResponseSchema,
  expenseReceiptPresignRequestSchema,
  expenseReceiptPresignResponseSchema,
  hasExactlyOneSubject,
  positiveMoneySchema,
} from './expense.schemas';

/** The driver app's expense calls (gitaumoses4/chukua#62). Additive to sync contract v1: see `sync-contract/additive/`. */

/**
 * `POST /sync/expenses`. The phone's own fields are all here (`tripId`, `kind`, `amountMinor`, `currency`, `note`); `kind` is read as
 * `categoryCode` for one release, until the app sends the code (#76). `clientExpenseId` is the phone's id for the expense and
 * `deviceTime` its clock. Both are optional because the held outbox entries were written without them: a missing `clientExpenseId` is read
 * from the `Idempotency-Key` (the phone keys an expense by its own id), and a missing `deviceTime` is the time the server received it.
 */
export const syncExpenseRequestSchema = z
  .object({
    clientExpenseId: z.uuid().optional(),
    deviceTime: z.iso.datetime({ offset: true }).optional(),
    tripId: z.uuid().optional(),
    truckId: z.uuid().optional(),
    categoryCode: expenseCategoryCodeSchema.optional(),
    kind: expenseCategoryCodeSchema.optional(),
    amountMinor: positiveMoneySchema.shape.amountMinor,
    currency: moneySchema.shape.currency,
    note: z.string().trim().max(500).optional(),
    fuel: expenseFuelDetailsSchema.optional(),
  })
  .refine(hasExactlyOneSubject, { message: EXPENSE_SUBJECT_MESSAGE, path: ['tripId'] })
  .refine((request) => Boolean(request.categoryCode ?? request.kind), { message: 'Give a categoryCode', path: ['categoryCode'] })
  .refine((request) => !request.categoryCode || !request.kind || request.categoryCode === request.kind, {
    message: 'categoryCode and kind disagree',
    path: ['kind'],
  })
  .refine((request) => request.amountMinor > 0, { message: 'An expense must be more than zero', path: ['amountMinor'] })
  .transform(({ kind, categoryCode, ...request }) => ({ ...request, categoryCode: (categoryCode ?? kind) as string }));
export type SyncExpenseRequest = z.output<typeof syncExpenseRequestSchema>;

export const syncExpenseResponseSchema = z.object({
  expenseId: z.uuid(),
  state: z.enum(EXPENSE_STATES),
  /** True when this `clientExpenseId` was recorded before, under another idempotency key. */
  alreadyRecorded: z.boolean(),
});
export type SyncExpenseResponse = z.infer<typeof syncExpenseResponseSchema>;

/** The phone sends the expense first and its photos after, against the id the first answer gave. */
export const syncExpenseReceiptPresignRequestSchema = expenseReceiptPresignRequestSchema.extend({ expenseId: z.uuid() });
export type SyncExpenseReceiptPresignRequest = z.output<typeof syncExpenseReceiptPresignRequestSchema>;
export const syncExpenseReceiptPresignResponseSchema = expenseReceiptPresignResponseSchema;
export type SyncExpenseReceiptPresignResponse = z.infer<typeof syncExpenseReceiptPresignResponseSchema>;

export const syncExpenseReceiptConfirmRequestSchema = expenseReceiptConfirmRequestSchema.extend({ expenseId: z.uuid() });
export type SyncExpenseReceiptConfirmRequest = z.output<typeof syncExpenseReceiptConfirmRequestSchema>;
export const syncExpenseReceiptConfirmResponseSchema = expenseReceiptConfirmResponseSchema;
export type SyncExpenseReceiptConfirmResponse = z.infer<typeof syncExpenseReceiptConfirmResponseSchema>;
